import { type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import {
  type RelationEditor,
  type WithId,
} from '@framework/form/form/relations/useRelationEditor.ts';
import { useCallback, useMemo, useState } from 'react';
import { useSmartSensors } from './useSmartSensors.tsx';

/**
 * Hook che ritorna i metodi base e più comunemente usati per gestire i drag and drop
 */
export function useBasicDndDragger<T>(editor: RelationEditor<T>) {
  const sensors = useSmartSensors();
  const [active, setActive] = useState<WithId<T> | null>(null);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = editor.items.findIndex(p => p.__uid === active.id);
        const newIndex = editor.items.findIndex(p => p.__uid === over?.id);
        editor.move(oldIndex, newIndex);
      }

      setActive(null);
    },
    [editor],
  );

  const itemIds = useMemo(
    () => editor.items?.map((p, i) => p.__uid) ?? [],
    [editor.items],
  );

  const handleDragStart = useCallback(
    (e: DragStartEvent) => {
      setActive(editor.items.find(p => p.__uid === e.active.id) ?? null);
    },
    [editor.items],
  );

  return {
    sensors,
    active,
    setActive,
    handleDragEnd,
    handleDragStart,
    itemIds,
  };
}

export type IBasicDndDragger = ReturnType<typeof useBasicDndDragger>;
